// config-editor.ts — THE configuration editor (Helm settings re-implementation,
// owner ruling 2026-09-08).
//
// "The page should match EXACTLY the configuration menu of a config template
// in the Bridge. All the components, layout, styles, animations should match
// pixel for pixel." The way that stays true is ONE implementation: this
// module is the editor the Bridge's config-template screen mounts
// (helm-console/src/client/config-templates.ts) AND the editor the Helm's
// SETTINGS tab mounts (helm-bridge/src/client/settings-tab.ts). Neither
// surface owns a copy. Its CSS is the `cfg-component-block`, present
// byte-identically in both stylesheets and gated by primitives-check —
// the same discipline the `.ibtn`, fc and feedback-nav blocks already hold.
//
// HISTORY: this is the Bridge's mountEditor (config-templates.ts, the
// settings-editor batch) lifted out with its console seams made parameters:
//   - the data plane: `catalog` + `saved` come in, `onSave(sparse, changes)`
//     goes out (the console PATCHes /api/config-templates/<id>; the Helm fans
//     the diff out per section to its own lanes);
//   - the audit-path spelling (`pathFor`, default `values.<section>.<key>`);
//   - the dialog host + vocabulary (the console's .hc-modal / the Helm's
//     .overlay — the shared modal-host engine takes either);
//   - the class prefix: `.cfg-*`, neutral, in the shared block.
// What did NOT change is the batch, each item traced to the owner's words
// then and kept now: the search box FITS its 240px column; the right pane
// scrolls under the shared element scroll rail; selects are the shared
// dropdown (portal lane — the cards clip overflow) and numbers a custom
// stepper, keyboard included; every setting carries the reset button (green
// disabled check at the default, morphing to the amber reset when the value
// differs); scroll-spy with ONE sliding indicator; search prunes rows/cards/
// nav rows through a two-phase hide. Every motion collapses to instant under
// prefers-reduced-motion.
//
// The DOM is built ONCE and updated IN PLACE from then on — a full innerHTML
// rebuild on every keystroke made every state change a teleport (no reset
// morph, no filter exit animation, no sliding selector can survive a rebuild).
//
// Controls: a native <input type="text"> for text/number and a <textarea>
// for json ride the block's own `.cfg-input`/`.cfg-json` rules — the same
// markup the Bridge shipped. Swapping them onto mkFcText/mkFcStepper would
// change the Bridge's pixels, which this extraction exists NOT to do; that
// swap is a named follow-up, not a side effect.

import { mkFcSelect, mkFcText, mkFcToggle, type FcSkin } from './form-controls.js';
import { iconBtn, mkIconBtn, setBtnGlyph, setBtnIntent, setBtnLabel, setBtnState } from './buttons.js';
import { prefersReducedMotion } from './motion.js';
import { attachElementScrollRail } from './scroll-rails.js';
import { mountDocNav } from './doc-nav.js';
import { openModal, showModalMsg, type ModalVocabulary } from './modal-host.js';
import {
  changedFromDefault, displayValue, filterCatalog, parseControlValue, pendingDiff, reachNote as defaultReachNote,
  resetVm, sectionNav, setDraft, shownValue, sparseFromDraft, stepNumber, stepperVm, valuesPath,
  type CfgSection, type CfgVariable, type PathFor, type PendingChange, type SectionsDoc,
} from './config-editor-vm.js';

export type {
  CfgSection, CfgVariable, PathFor, PendingChange, SectionsDoc, VarType,
} from './config-editor-vm.js';

// ---------------------------------------------------------------------------
// The token contract. Every colour in the cfg-component-block rides one of
// these `--cfg-*` names; the Helm authors them from --surface/--ink/…, the
// console from --hc-*. The NAMES are the contract; primitives-check refuses a
// home that leaves one undefined, and a block that drifts between homes.
// ---------------------------------------------------------------------------
export const CFG_TOKEN_SLOTS = [
  'fg', 'muted', 'panel', 'surface', 'surface-2', 'line', 'hairline', 'radius',
  'accent', 'accent-dim', 'danger',
  'badge-info-bg', 'badge-info-fg', 'badge-warn-bg', 'badge-warn-fg', 'badge-neutral-bg', 'badge-neutral-fg',
] as const;

/** The editor's dropdown LAYOUT hooks, stamped beside the fc-* classes. The
 *  paint is the fc-component-block's; these carry size and position only
 *  (primitives-check's layout-only arm refuses a skin class that paints). */
export const CFG_DD_SKIN = {
  wrap: 'cfg-dd',
  btn: 'cfg-dd-btn',
  label: 'cfg-dd-label',
  chev: 'cfg-dd-chev',
  pop: 'cfg-dd-pop',
  item: 'cfg-dd-item',
} as const satisfies FcSkin;

/** The editor's search-box layout hooks — the interaction ladder (icon lift,
 *  typing tick, clear-in spring) lives in the cfg block under these names. */
export const CFG_SEARCH_SKIN = {
  wrap: 'cfg-search',
  input: 'cfg-search-input',
  ico: 'cfg-sb-ico',
  clear: 'cfg-sb-clear',
  has: 'cfg-sb-has',
  typing: 'cfg-sb-typing',
} as const satisfies FcSkin;

/** How long the filter's two-phase hide waits before display:none — the
 * transition is --t-standard (220ms); the timer only needs to outlive it. */
export const CFG_HIDE_MS = 240;

export type SaveResult =
  | { ok: true; saved: SectionsDoc }
  | { ok: false; error: string };

export interface ConfigEditorOpts {
  /** The header row the dirty line + Discard + Save land in (appended after
   *  whatever the caller already put there — a back link, a title, badges). */
  header: HTMLElement;
  /** The page body the `.cfg-editor` grid mounts into (replaces its content). */
  body: HTMLElement;
  /** The dialog host (the console's `.hc-modal`, the Helm's `.overlay`). */
  modal: HTMLElement;
  /** That host's class vocabulary (HC_VOCAB / HELM_VOCAB). */
  vocab: ModalVocabulary;
  catalog: CfgSection[];
  saved: SectionsDoc;
  /** false = the read-only view: controls disabled, no reset, no Save. */
  canManage: boolean;
  /** SAVE: receives the pruned sparse doc and the review rows. Return the doc
   *  the store now holds (the editor re-seats itself on it) or a NAMED error
   *  the review dialog shows in place. */
  onSave: (sparse: SectionsDoc, changes: PendingChange[]) => Promise<SaveResult>;
  /** The audit-path spelling for a row (`data-path`, the review rows). */
  pathFor?: PathFor;
  /** The one-line honesty label under a section's blurb. */
  reachNote?: (s: CfgSection) => string | null;
  searchPlaceholder?: string;
  searchLabel?: string;
  discardConfirm?: string;
  /** The review dialog's copy. */
  reviewBlurb?: string;
  /** Called after every successful save with the doc now held. */
  onSaved?: (saved: SectionsDoc) => void;
}

export interface ConfigEditorHandle {
  /** The `.cfg-editor` root. */
  el: HTMLElement;
  dirtyCount(): number;
  invalidCount(): number;
  /** Replace the saved doc (an external reload) — draft and invalid rows are
   *  dropped and every control re-seated. */
  setSaved(doc: SectionsDoc): void;
  /** Discard every unsaved edit (the header button's lane). */
  discard(): void;
  /** Open the Review & save dialog (the header button's lane). */
  review(): void;
  /** Scroll the pane to a section and select its nav row. */
  navigateTo(sectionId: string): void;
  destroy(): void;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const svg = (glyph: string): string => `<svg class="icon" aria-hidden="true"><use href="#i-${esc(glyph)}"/></svg>`;

export function mountConfigEditor(opts: ConfigEditorOpts): ConfigEditorHandle {
  const pathFor = opts.pathFor ?? valuesPath;
  const reachNote = opts.reachNote ?? defaultReachNote;
  const { catalog, modal, vocab } = opts;
  const manage = opts.canManage;
  let saved: SectionsDoc = opts.saved;
  let draft: SectionsDoc = {};
  /** Rows whose raw input failed parseControlValue: path -> {raw, error}.
   * Save is blocked while any exist — an invalid row is a NAMED local
   * refusal, never silently dropped. */
  const invalid = new Map<string, { raw: string; error: string }>();

  // ---- header trio ---------------------------------------------------------
  const headFrag = document.createElement('span');
  headFrag.className = 'cfg-head';
  headFrag.innerHTML = `
    <span class="cfg-dirty" id="cfgDirty" aria-live="polite"></span>
    ${iconBtn({
      glyph: 'list-restart', label: 'Discard changes', intent: 'destructive', size: 'sm',
      confirm: opts.discardConfirm ?? 'Discard every unsaved edit?', id: 'cfgDiscard', attrs: { hidden: true },
    })}
    ${iconBtn({
      glyph: 'check', label: 'Review & save', intent: 'additive', ctx: 'config-editor',
      size: 'sm', id: 'cfgSave', attrs: { hidden: true },
    })}`;
  opts.header.appendChild(headFrag);
  const dirtyEl = headFrag.querySelector('#cfgDirty') as HTMLElement;
  const discardBtn = headFrag.querySelector('#cfgDiscard') as HTMLButtonElement;
  const saveBtn = headFrag.querySelector('#cfgSave') as HTMLButtonElement;

  // ---- body ----------------------------------------------------------------
  opts.body.innerHTML = `
    <div class="cfg-editor">
      <div class="cfg-side">
        <nav class="cfg-nav" id="cfgNav" aria-label="Sections"></nav>
      </div>
      <div class="cfg-contentwrap">
        <div class="cfg-content" id="cfgContent" tabindex="-1"></div>
      </div>
    </div>`;
  const root = opts.body.querySelector('.cfg-editor') as HTMLElement;
  const sideEl = root.querySelector('.cfg-side') as HTMLElement;
  const navEl = root.querySelector('#cfgNav') as HTMLElement;
  const wrapEl = root.querySelector('.cfg-contentwrap') as HTMLElement;
  const contentEl = root.querySelector('#cfgContent') as HTMLElement;

  // The ONE search component — the shared text primitive's search variant,
  // wearing the block's layout hooks so it FITS the 240px column.
  const total = catalog.reduce((n, s) => n + s.variables.length, 0);
  const search = mkFcText({
    id: 'cfgFind',
    label: opts.searchLabel ?? 'Search settings',
    placeholder: opts.searchPlaceholder ?? `Search ${total >= 10 ? `${Math.floor(total / 10) * 10}+` : total} settings`,
    variant: 'search',
    skin: CFG_SEARCH_SKIN,
    onInput: () => applySearch(),
  });
  sideEl.prepend(search.el);
  const query = (): string => search.value;

  // The custom scroll rail on the RIGHT pane only (the left nav keeps its
  // own scroll untouched — the owner's split).
  const rail = attachElementScrollRail(contentEl, { host: wrapEl });

  function dirtyCount(): number {
    return pendingDiff(catalog, saved, draft, pathFor).length;
  }

  function renderHeader(): void {
    const n = dirtyCount();
    const bad = invalid.size;
    dirtyEl.textContent = bad
      ? `${bad} invalid value${bad === 1 ? '' : 's'}`
      : n ? `${n} unsaved change${n === 1 ? '' : 's'}` : '';
    dirtyEl.classList.toggle('cfg-dirty-bad', bad > 0);
    if (manage) {
      discardBtn.toggleAttribute('hidden', n === 0 && bad === 0);
      saveBtn.toggleAttribute('hidden', n === 0);
      saveBtn.disabled = bad > 0;
    }
  }

  // ---- two-phase show/hide (the filter animation's one mechanism) --------
  const hideTimers = new Map<HTMLElement, number>();
  function setShown(target: HTMLElement, shown: boolean): void {
    const t = hideTimers.get(target);
    if (t !== undefined) { clearTimeout(t); hideTimers.delete(target); }
    if (prefersReducedMotion()) {
      target.classList.remove('cfg-out');
      target.hidden = !shown;
      return;
    }
    if (shown) {
      if (target.hidden) {
        // Return: un-hide transparent, then release to natural in-place.
        target.hidden = false;
        target.classList.add('cfg-out');
        requestAnimationFrame(() => requestAnimationFrame(() => target.classList.remove('cfg-out')));
      } else {
        target.classList.remove('cfg-out');
      }
    } else if (!target.hidden) {
      target.classList.add('cfg-out');
      hideTimers.set(target, window.setTimeout(() => {
        target.hidden = true;
        hideTimers.delete(target);
      }, CFG_HIDE_MS));
    }
  }

  // ---- persistent row/card/nav records ------------------------------------
  interface RowRec {
    v: CfgVariable;
    sectionId: string;
    path: string;
    row: HTMLElement;
    /** Push a PROGRAMMATIC value into the control (reset / discard). */
    setControl(value: unknown): void;
    /** Re-derive classes, hint, reset face and stepper bounds in place. */
    sync(): void;
  }
  const rows = new Map<string, RowRec>();
  const cards = new Map<string, HTMLElement>();
  const customTeardowns: Array<() => void> = [];
  interface NavRec { a: HTMLAnchorElement; shownEl: HTMLElement; changedEl: HTMLElement; dirtyEl: HTMLElement }
  const navRows = new Map<string, NavRec>();
  let navInd: HTMLElement = el('span', 'cfg-navind');
  let emptyEl: HTMLElement = el('p', 'cfg-empty');

  function commitTyped(v: CfgVariable, sectionId: string, value: unknown): void {
    const path = pathFor(sectionId, v.key);
    invalid.delete(path);
    draft = setDraft(draft, sectionId, v.key, value);
    rows.get(path)?.sync();
    refreshNav();
    renderHeader();
  }

  function commit(v: CfgVariable, sectionId: string, raw: string): void {
    const path = pathFor(sectionId, v.key);
    const parsed = parseControlValue(v, raw);
    if (parsed.error) {
      invalid.set(path, { raw, error: parsed.error });
      rows.get(path)?.sync();
      refreshNav();
      renderHeader();
    } else {
      commitTyped(v, sectionId, parsed.value);
    }
  }

  interface Ctl { el: HTMLElement; set(value: unknown): void; sync?(): void }

  function controlFor(v: CfgVariable, sectionId: string, value: unknown, path: string): Ctl {
    const bad = invalid.get(path);
    if (v.type === 'toggle') {
      const tg = mkFcToggle({
        label: v.label,
        text: '',
        // A locked variable shows its fixed value and cannot be changed (D6b).
        checked: v.locked ? v.default === true : value === true,
        disabled: !manage || !!v.locked,
        cls: 'cfg-toggle',
        onChange: (on) => commitTyped(v, sectionId, on),
      });
      return { el: tg.el, set: (val) => { tg.checked = val === true; } };
    }
    if (v.type === 'select') {
      // The shared dropdown — the full keyboard contract lives there: arrows
      // open/rove, Enter picks, Escape closes. Portal lane: the cards clip
      // (overflow:hidden for the radius), so the pop must escape to body.
      const dd = mkFcSelect({
        id: `cfgDd-${`${sectionId}-${v.key}`.replace(/[^A-Za-z0-9_-]/g, '-')}`,
        label: v.label,
        options: () => (v.options ?? []).map((o) => ({ value: o.value, label: o.label })),
        value: () => String(shownValue(v, sectionId, saved, draft)),
        onPick: (val) => commit(v, sectionId, val),
        portal: true,
        skin: CFG_DD_SKIN,
      });
      if (!manage) dd.btn.disabled = true;
      return { el: dd.el, set: () => dd.set() };
    }
    if (v.type === 'json') {
      const ta = document.createElement('textarea');
      ta.className = 'cfg-json';
      ta.rows = 3;
      ta.spellcheck = false;
      ta.value = bad ? bad.raw : JSON.stringify(value, null, 2) ?? 'null';
      ta.disabled = !manage;
      ta.setAttribute('aria-label', v.label);
      ta.addEventListener('change', () => commit(v, sectionId, ta.value));
      return { el: ta, set: (val) => { ta.value = JSON.stringify(val, null, 2) ?? 'null'; } };
    }
    if (v.type === 'number') {
      // Custom stepper: text input + chevron column. Arrows (buttons AND
      // ArrowUp/ArrowDown on the input) step by v.step and CLAMP at the
      // bounds — a bounded gesture; typed values keep parseControlValue's
      // refuse-don't-clamp posture.
      const wrap = el('div', 'cfg-step');
      const input = document.createElement('input');
      input.className = 'cfg-input';
      input.type = 'text';
      input.inputMode = 'decimal';
      input.setAttribute('role', 'spinbutton');
      input.setAttribute('aria-label', v.label);
      if (v.min !== undefined) input.setAttribute('aria-valuemin', String(v.min));
      if (v.max !== undefined) input.setAttribute('aria-valuemax', String(v.max));
      input.disabled = !manage;
      input.value = bad ? bad.raw : String(value ?? '');
      input.addEventListener('change', () => commit(v, sectionId, input.value));
      const btns = el('div', 'cfg-stepbtns');
      const step = (dir: 1 | -1): void => {
        if (!manage) return;
        input.value = stepNumber(v, input.value, dir);
        commit(v, sectionId, input.value);
      };
      const mkBtn = (dir: 1 | -1, glyph: string, verb: string): HTMLButtonElement => {
        const b = document.createElement('button');
        b.type = 'button';
        b.dataset.btnRole = 'stepper';
        b.className = 'cfg-stepbtn';
        b.tabIndex = -1; // the input is the tab stop; its arrow keys are the keyboard lane
        b.innerHTML = svg(glyph);
        b.setAttribute('aria-label', `${verb} ${v.label}`);
        b.addEventListener('click', () => step(dir));
        btns.appendChild(b);
        return b;
      };
      const up = mkBtn(1, 'chevron-up', 'Increase');
      const down = mkBtn(-1, 'chevron-down', 'Decrease');
      input.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        step(e.key === 'ArrowUp' ? 1 : -1);
      });
      wrap.append(input, btns);
      const syncStep = (): void => {
        const s = stepperVm(v, input.value.trim() === '' ? NaN : Number(input.value));
        up.disabled = !manage || !s.canUp;
        down.disabled = !manage || !s.canDown;
        input.setAttribute('aria-valuenow', input.value);
      };
      syncStep();
      return { el: wrap, set: (val) => { input.value = String(val ?? ''); syncStep(); }, sync: syncStep };
    }
    const input = document.createElement('input');
    input.className = 'cfg-input';
    input.type = 'text';
    input.disabled = !manage;
    input.setAttribute('aria-label', v.label);
    input.value = bad ? bad.raw : String(value ?? '');
    input.addEventListener('change', () => commit(v, sectionId, input.value));
    return { el: input, set: (val) => { input.value = String(val ?? ''); } };
  }

  function buildRow(section: CfgSection, v: CfgVariable): RowRec {
    const path = pathFor(section.id, v.key);
    const value = shownValue(v, section.id, saved, draft);
    const row = el('div', 'cfg-row');
    row.setAttribute('data-path', path);
    const left = el('div', 'cfg-rowtext');
    const lbl = el('span', 'cfg-label', v.label);
    const dot = el('span', 'cfg-dot', '');
    dot.hidden = true;
    lbl.appendChild(dot);
    left.appendChild(lbl);
    left.appendChild(el('span', 'cfg-help', v.help));
    if (v.locked) {
      row.setAttribute('data-locked', '1');
      left.appendChild(el('span', 'cfg-locked', v.locked));
    }
    const errEl = el('span', 'cfg-error');
    errEl.hidden = true;
    left.appendChild(errEl);
    const hintEl = el('span', 'cfg-defhint', `default: ${displayValue(v, v.default)}`);
    hintEl.hidden = true;
    left.appendChild(hintEl);
    row.appendChild(left);

    const ctlWrap = el('div', 'cfg-ctl');
    const ctl = controlFor(v, section.id, value, path);
    ctlWrap.appendChild(ctl.el);
    // The per-setting reset: resetVm decides presence (no button for a
    // read-only viewer, none for a default-less variable) and the face
    // (green check at default, amber reset when changed). The morph is CSS
    // on ONE persistent `.ibtn` — inert at the default, error-ramp when the
    // value differs.
    let resetBtn: HTMLButtonElement | null = null;
    if (!v.locked && resetVm(v, value, manage).present) {
      resetBtn = mkIconBtn({
        glyph: 'circle-check', label: 'At the default', state: 'disabled', size: 'sm',
        cls: 'cfg-resetbtn',
      });
      resetBtn.addEventListener('click', () => {
        if (resetBtn!.dataset.state !== 'changed') return;
        invalid.delete(path);
        draft = setDraft(draft, section.id, v.key, v.default);
        rec.setControl(v.default);
        rec.sync();
        refreshNav();
        renderHeader();
      });
      ctlWrap.appendChild(resetBtn);
    }
    row.appendChild(ctlWrap);

    const rec: RowRec = {
      v,
      sectionId: section.id,
      path,
      row,
      setControl: (val) => { ctl.set(val); },
      sync: () => {
        const bad = invalid.get(path);
        const val = shownValue(v, section.id, saved, draft);
        const changed = changedFromDefault(v, val);
        row.classList.toggle('cfg-row-changed', changed && !bad);
        row.classList.toggle('cfg-row-bad', !!bad);
        dot.hidden = !changed && !bad;
        errEl.textContent = bad ? bad.error : '';
        errEl.hidden = !bad;
        hintEl.hidden = !changed && !bad;
        if (resetBtn) {
          const rv = resetVm(v, bad ? bad.raw : val, manage);
          const state = bad ? 'changed' : rv.state;
          const tip = state === 'atdefault'
            ? rv.tip : `Reset to default (${displayValue(v, v.default)})`;
          resetBtn.dataset.state = state;
          setBtnGlyph(resetBtn, state === 'atdefault' ? 'circle-check' : 'list-restart');
          setBtnIntent(resetBtn, state === 'atdefault' ? 'neutral' : 'error');
          setBtnState(resetBtn, state === 'atdefault' ? 'disabled' : 'enabled');
          setBtnLabel(resetBtn, tip);
        }
        ctl.sync?.();
      },
    };
    rec.sync();
    rows.set(path, rec);
    return rec;
  }

  function buildContent(): void {
    for (const t of customTeardowns.splice(0)) t();
    contentEl.innerHTML = '';
    rows.clear();
    cards.clear();
    for (const section of catalog) {
      const card = el('section', 'cfg-card');
      card.setAttribute('data-sect', section.id);
      const head = el('header', 'cfg-cardhead');
      head.appendChild(el('h2', 'cfg-cardtitle', section.title));
      head.appendChild(el('p', 'cfg-cardblurb', section.blurb));
      const reach = reachNote(section);
      if (reach) {
        const note = el('p', `cfg-cardreach cfg-reach-${section.reach ?? 'note'}`, reach);
        head.appendChild(note);
      }
      card.appendChild(head);
      for (const v of section.variables) card.appendChild(buildRow(section, v).row);
      if (section.render) {
        const body = el('div', 'cfg-custom');
        card.appendChild(body);
        const t = section.render(body);
        if (t) customTeardowns.push(t);
      }
      cards.set(section.id, card);
      contentEl.appendChild(card);
    }
    emptyEl = el('p', 'cfg-empty');
    emptyEl.hidden = true;
    contentEl.appendChild(emptyEl);
    rail.refresh();
  }

  function buildNav(): void {
    navEl.innerHTML = '';
    navRows.clear();
    navInd = el('span', 'cfg-navind');
    navEl.appendChild(navInd);
    for (const s of catalog) {
      const a = el('a', 'cfg-navrow') as HTMLAnchorElement;
      a.href = `#sect-${s.id}`;
      a.setAttribute('data-sect', s.id);
      a.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        navigateTo(s.id);
      });
      a.appendChild(el('span', 'cfg-navtitle', s.title));
      const meta = el('span', 'cfg-navmeta');
      const shownEl = el('span', 'cfg-navshown');
      const changedEl = el('span', 'cfg-navchanged');
      const dirtyBadge = el('span', 'cfg-navdirty');
      shownEl.hidden = changedEl.hidden = dirtyBadge.hidden = true;
      meta.append(shownEl, changedEl, dirtyBadge);
      a.appendChild(meta);
      navEl.appendChild(a);
      navRows.set(s.id, { a, shownEl, changedEl, dirtyEl: dirtyBadge });
    }
  }

  function refreshNav(): void {
    const q = query();
    for (const e of sectionNav(catalog, saved, draft, q)) {
      const r = navRows.get(e.id);
      if (!r) continue;
      setShown(r.a, !(e.shown === 0 && q.trim() !== ''));
      r.shownEl.hidden = !(q.trim() !== '' && e.shown < e.total);
      r.shownEl.textContent = `${e.shown}/${e.total}`;
      r.changedEl.hidden = e.changed === 0;
      r.changedEl.textContent = String(e.changed);
      r.dirtyEl.hidden = e.dirty === 0;
      r.dirtyEl.textContent = String(e.dirty);
    }
    scheduleIndicator();
  }

  function applySearch(): void {
    const q = query();
    const filtered = filterCatalog(catalog, q);
    const keepRows = new Set<string>();
    const keepSects = new Set<string>();
    for (const f of filtered) {
      keepSects.add(f.section.id);
      for (const v of f.variables) keepRows.add(pathFor(f.section.id, v.key));
    }
    for (const [path, rec] of rows) setShown(rec.row, keepRows.has(path));
    for (const [sid, card] of cards) setShown(card, keepSects.has(sid));
    emptyEl.hidden = filtered.length !== 0;
    if (filtered.length === 0) {
      emptyEl.textContent =
        `No settings match '${q.trim()}' — searched name, key, help and seat across every section.`;
    }
    refreshNav();
    spy.sweep();
    // Re-measure once the two-phase hide has settled: the rail's extent and
    // the cards' offsets both changed.
    window.setTimeout(() => { rail.refresh(); spy.sweep(); }, CFG_HIDE_MS + 20);
  }

  // ---- scroll-spy + the sliding selector ----------------------------------
  // The MECHANISM (band decision, rAF coalescing, click-driven selection
  // LOCK, scrollend release, safety timer, reduced-motion arm) is the shared
  // primitive's (attachScrollSpy), and since #810 the composition every
  // consumer wrote on top of it — measure the parts, find the row, stamp
  // `.on` + aria-current, unstamp the last, turn a click into a locked
  // navigateTo — is the shared `mountDocNav` (doc-nav.ts), which the Helm's
  // corpus release document consumes too. What is this view's: WHERE the
  // sections are (the non-hidden cards), WHICH row answers for one, and the
  // one sliding indicator pill.
  let activeId: string | null = null;

  function positionIndicator(): void {
    const r = activeId ? navRows.get(activeId) : null;
    if (!r || r.a.hidden) { navInd.classList.remove('on'); return; }
    navInd.style.top = `${r.a.offsetTop}px`;
    navInd.style.height = `${r.a.offsetHeight}px`;
    navInd.classList.add('on');
  }
  function scheduleIndicator(): void {
    requestAnimationFrame(positionIndicator);
    // Nav rows may still be mid-hide; reposition once they have collapsed.
    window.setTimeout(positionIndicator, CFG_HIDE_MS + 20);
  }

  const spy = mountDocNav({
    scroller: contentEl,
    anchors: () => {
      const parts: { id: string; el: HTMLElement }[] = [];
      for (const sec of catalog) {
        const card = cards.get(sec.id);
        if (card && !card.hidden) parts.push({ id: sec.id, el: card });
      }
      return parts;
    },
    rowFor: (sid) => navRows.get(sid)?.a ?? null,
    onActive: (sid) => {
      activeId = sid;
      positionIndicator();
    },
  });

  function navigateTo(sid: string): void {
    const card = cards.get(sid);
    if (!card || card.hidden) return;
    spy.go(sid);
  }

  function syncAllRows(): void {
    for (const rec of rows.values()) {
      rec.setControl(shownValue(rec.v, rec.sectionId, saved, draft));
      rec.sync();
    }
  }

  function discard(): void {
    draft = {};
    invalid.clear();
    syncAllRows();
    refreshNav();
    renderHeader();
  }

  function reviewDialog(): void {
    const changes = pendingDiff(catalog, saved, draft, pathFor);
    if (changes.length === 0) return;
    openModal(modal, (body, close) => {
      body.appendChild(el('h2', vocab.title, `Save ${changes.length} change${changes.length === 1 ? '' : 's'}?`));
      body.appendChild(el('p', 'cfg-review-blurb',
        opts.reviewBlurb ?? 'These rows are exactly what the audit trail will record — old and new, per field.'));
      const list = el('div', 'cfg-review');
      const defs = new Map<string, CfgVariable>();
      for (const s of catalog) for (const v of s.variables) defs.set(pathFor(s.id, v.key), v);
      for (const c of changes) {
        const varDef = defs.get(c.path);
        const row = el('div', 'cfg-revrow');
        row.appendChild(el('span', 'cfg-revwhere', `${c.sectionTitle} · ${c.label}`));
        const vals = el('span', 'cfg-revvals');
        vals.appendChild(el('del', 'cfg-revold', varDef ? displayValue(varDef, c.old) : JSON.stringify(c.old)));
        vals.appendChild(el('span', 'cfg-revarrow', '→'));
        vals.appendChild(el('ins', 'cfg-revnew', varDef ? displayValue(varDef, c.new) : JSON.stringify(c.new)));
        row.appendChild(vals);
        list.appendChild(row);
      }
      body.appendChild(list);
      const msg = el('p', 'cfg-review-msg');
      body.appendChild(msg);
      const actions = el('div', vocab.actions);
      const cancel = mkIconBtn({ glyph: 'x', label: 'Keep editing' });
      cancel.addEventListener('click', close);
      const save = mkIconBtn({
        glyph: 'check', label: `Save ${changes.length} change${changes.length === 1 ? '' : 's'}`,
        intent: 'additive', ctx: 'config-editor-review',
      });
      save.addEventListener('click', () => {
        setBtnState(save, 'busy');
        void opts.onSave(sparseFromDraft(catalog, draft), changes).then((r) => {
          setBtnState(save, 'enabled');
          if (!r.ok) return showModalMsg(msg, r.error, vocab.msgIn);
          saved = r.saved;
          draft = {};
          invalid.clear();
          close();
          syncAllRows();
          refreshNav();
          renderHeader();
          opts.onSaved?.(saved);
        }, (e: unknown) => {
          setBtnState(save, 'enabled');
          showModalMsg(msg, String((e as { message?: string })?.message ?? e), vocab.msgIn);
        });
      });
      actions.appendChild(cancel);
      actions.appendChild(save);
      body.appendChild(actions);
    }, {}, vocab);
  }

  discardBtn.addEventListener('click', () => discard());
  saveBtn.addEventListener('click', () => reviewDialog());

  renderHeader();
  buildNav();
  buildContent();
  refreshNav();
  spy.sweep(); // seat the indicator on the section that owns the viewport

  return {
    el: root,
    dirtyCount,
    invalidCount: () => invalid.size,
    setSaved(doc) {
      saved = doc;
      draft = {};
      invalid.clear();
      syncAllRows();
      refreshNav();
      renderHeader();
    },
    discard,
    review: reviewDialog,
    navigateTo,
    destroy() {
      for (const t of customTeardowns.splice(0)) t();
      for (const t of hideTimers.values()) clearTimeout(t);
      hideTimers.clear();
      spy.destroy();
      rail.destroy();
      headFrag.remove();
    },
  };
}
